/**
 * Footer 컴포넌트 (뉴스 스타일)
 * 회사소개 | 서비스 | 비즈니스 | 문의 구조
 */

import { ExternalLink, Facebook, Instagram, Mail, Youtube } from "lucide-react";
import { Link } from "wouter";

export function Footer() {
  const currentYear = new Date().getFullYear();

  // 회사 정보
  const companyInfo = {
    name: "OZK NEWS",
    company: "엠알티",
    representative: "강하균",
    businessNumber: "464-51-00773",
    email: "ozknews@gmail.com",
    address: "경기도 성남시 분당구 야탑로245번길 11, 101동 306호",
    sydneyOffice: "160 Midson Road, Epping NSW 2121, Australia",
    hosting: "Cloudflare, Inc (cloudflare.com)",
  };

  // 회사 링크
  const companyLinks = [
    { label: "회사 소개", href: "/about" },
    { label: "서비스 소개", href: "/services" },
    { label: "팀 문화", href: "/team" },
    { label: "블로그", href: "/blog" },
    { label: "채용", href: "/careers" },
  ];

  // 탐색 링크
  const exploreLinks = [
    { label: "동네 톡톡", href: "/community-talk" },
    { label: "생활문제 해결 가이드", href: "/topics" },
    { label: "동네 마켓", href: "/market" },
    { label: "자동차", href: "/market?category=used-car" },
    { label: "부동산", href: "/market?category=real-estate" },
    { label: "구인/구직", href: "/jobs" },
    { label: "동네업체", href: "/business-directory" },
    { label: "교민모임", href: "/meetings" },
    { label: "채팅하기", href: "/chat" },
  ];

  // 비즈니스 링크
  const businessLinks = [
    { label: "OZK 비즈니스", href: "/business" },
    { label: "제휴 문의", href: "/partnership" },
    { label: "광고 문의", href: "/advertise" },
  ];

  // 패밀리 (글로벌 서비스)
  const globalServices = [
    { label: "Canada", href: "https://caknews.com", external: true },
    { label: "United States", href: "https://usknews.com", external: true },
    { label: "United Kingdom", href: "https://ukknews.com", external: true },
  ];

  // 문의 링크
  const inquiryLinks = [{ label: "고객센터", href: "/support" }];

  // 정책 링크
  const policyLinks = [
    { label: "이용약관", href: "/terms" },
    { label: "개인정보처리방침", href: "/privacy" },
    { label: "운영정책", href: "/policy" },
    { label: "위치기반서비스 이용약관", href: "/location-terms" },
    { label: "이용자보호 비전과 계획", href: "/user-protection" },
    { label: "청소년보호정책", href: "/youth" },
    { label: "OZK 콘텐츠 제작 및 저작권 정책", href: "/editorial-policy" },
  ];

  return (
    <footer className="bg-background text-muted-foreground mt-auto">
      <div className="mx-auto w-full max-w-[1536px] px-4 py-12 md:px-6 lg:px-8">
        {/* Top Section: Logo & Social Media */}
        <div className="flex flex-col md:flex-row md:items-start gap-8 mb-12 pb-8 border-b border-border">
          <div className="space-y-4 md:w-[30%]">
            <Link href="/">
              <div className="cursor-pointer group">
                <img
                  src="/logo.png"
                  alt="OZK News"
                  className="h-8 w-auto object-contain"
                />
              </div>
            </Link>
            <div className="flex items-center gap-2">
              <a
                href="https://facebook.com/ozknews"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4 text-muted-foreground" />
              </a>
              <a
                href="https://instagram.com/ozknews"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4 text-muted-foreground" />
              </a>
              <a
                href="https://youtube.com/@ozknews"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
                aria-label="YouTube"
              >
                <Youtube className="w-4 h-4 text-muted-foreground" />
              </a>
            </div>
          </div>

          {/* Links Grid - 5 Columns */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:w-[70%]">
            {/* 회사 */}
            <div className="space-y-1.5">
              <h3 className="text-xs text-foreground font-medium">회사</h3>
              <ul className="space-y-1">
                {companyLinks.map((link) => (
                  <li key={link.href}>
                    <Link href={link.href}>
                      <span className="text-xs text-foreground hover:text-muted-foreground transition-colors cursor-pointer">
                        {link.label}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* 탐색 */}
            <div className="space-y-1.5">
              <h3 className="text-xs text-foreground font-medium">탐색</h3>
              <ul className="space-y-1">
                {exploreLinks.map((link) => (
                  <li key={link.href}>
                    <Link href={link.href}>
                      <span className="text-xs text-foreground hover:text-muted-foreground transition-colors cursor-pointer">
                        {link.label}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* 비즈니스 */}
            <div className="space-y-1.5">
              <h3 className="text-xs text-foreground font-medium">비즈니스</h3>
              <ul className="space-y-1">
                {businessLinks.map((link) => (
                  <li key={link.href}>
                    <Link href={link.href}>
                      <span className="text-xs text-foreground hover:text-muted-foreground transition-colors cursor-pointer">
                        {link.label}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* 패밀리 (글로벌) */}
            <div className="space-y-1.5">
              <h3 className="text-xs text-foreground font-medium">패밀리</h3>
              <ul className="space-y-1">
                {globalServices.map((link) => (
                  <li key={link.href}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-foreground hover:text-muted-foreground transition-colors flex items-center gap-1"
                      >
                        {link.label}
                        <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    ) : (
                      <Link href={link.href}>
                        <span className="text-xs text-foreground hover:text-muted-foreground transition-colors cursor-pointer">
                          {link.label}
                        </span>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* 문의 */}
            <div className="space-y-1.5">
              <h3 className="text-xs text-foreground font-medium">문의</h3>
              <ul className="space-y-1">
                {inquiryLinks.map((link) => (
                  <li key={link.href}>
                    <Link href={link.href}>
                      <span className="text-xs text-foreground hover:text-muted-foreground transition-colors cursor-pointer">
                        {link.label}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Section: Company Details & Legal Links */}
        <div className="flex flex-col gap-4">
          {/* Company Info - 각 항목마다 줄바꿈 */}
          <div className="text-[10px] text-muted-foreground space-y-0.5">
            <div>서비스명: {companyInfo.name}</div>
            <div>운영사업자: {companyInfo.company}</div>
            <div>대표자: {companyInfo.representative}</div>
            <div>사업자등록번호: {companyInfo.businessNumber}</div>
            <div>[Korea] {companyInfo.address}</div>
            <div>[Sydney] {companyInfo.sydneyOffice}</div>
            <div className="flex items-center gap-1">
              <Mail className="w-3 h-3" />
              <a
                href={`mailto:${companyInfo.email}`}
                className="hover:text-foreground transition-colors"
              >
                {companyInfo.email}
              </a>
            </div>
          </div>

          {/* Policy Links - 줄바꿈 후 위치 */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
            {policyLinks.map((link, index) => (
              <span key={link.href} className="flex items-center">
                <Link href={link.href}>
                  <span className="hover:text-foreground transition-colors cursor-pointer">
                    {link.label}
                  </span>
                </Link>
                {index < policyLinks.length - 1 && (
                  <span className="ml-4 text-muted-foreground/60">|</span>
                )}
              </span>
            ))}
          </div>

          {/* Copyright */}
          <div className="text-[10px] text-muted-foreground mt-2">
            © {currentYear} {companyInfo.name} operated by MRT. All rights
            reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}
